import React, { useEffect, useState } from 'react';
import { X, Activity, RefreshCw } from 'lucide-react';
import { worksApiService } from '../../services/workApi.service';

// Per-event-type styling for the activity timeline (mirrors WorkDetails).
const TL_META: Record<string, { dot: string; badge: string }> = {
  CREATE: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700' },
  UPDATE: { dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700' },
  STAGE: { dot: 'bg-purple-500', badge: 'bg-purple-100 text-purple-700' },
  PROGRESS: { dot: 'bg-cyan-500', badge: 'bg-cyan-100 text-cyan-700' },
  VERIFIED: { dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  SANCTION: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700' },
  AGREEMENT: { dot: 'bg-indigo-500', badge: 'bg-indigo-100 text-indigo-700' },
  FUND: { dot: 'bg-teal-500', badge: 'bg-teal-100 text-teal-700' },
  INSPECTION: { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700' },
  DELETE: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
  RESTORE: { dot: 'bg-gray-500', badge: 'bg-gray-100 text-gray-700' },
  _default: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' },
};

interface Props {
  workId: number | null;
  workName?: string;
  onClose: () => void;
}

const WorkTimelineModal: React.FC<Props> = ({ workId, workName, onClose }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (workId == null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    worksApiService
      .getWorkTimeline(workId)
      .then((t) => { if (alive) setEvents(t?.events || []); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'Failed to load timeline'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workId]);

  if (workId == null) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-base font-bold text-gray-800 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-600" /> Activity Timeline
            {workName && <span className="ml-2 text-xs font-normal text-gray-500 truncate max-w-xs">· {workName}</span>}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500"><RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2">{error}</div>
          ) : events.length === 0 ? (
            <p className="text-sm text-gray-500">No activity recorded yet.</p>
          ) : (
            <ol className="relative border-l-2 border-gray-200 ml-3 space-y-5">
              {events.map((e, i) => {
                const meta = TL_META[e.type] || TL_META._default;
                return (
                  <li key={i} className="ml-5">
                    <span className={`absolute -left-[9px] w-4 h-4 rounded-full ring-4 ring-white ${meta.dot}`} />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${meta.badge}`}>{e.type}</span>
                      <span className="text-sm font-medium text-gray-800">{e.title}</span>
                      <span className="text-xs text-gray-400">· {new Date(e.at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      by <span className="font-medium text-gray-700">{e.actor_name}</span>{e.detail ? ` · ${e.detail}` : ''}
                    </div>
                    {Array.isArray(e.changes) && e.changes.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {e.changes.slice(0, 8).map((c: any, j: number) => (
                          <span key={j} className="text-[11px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-gray-600">
                            {c.field}: <span className="text-gray-400">{c.from ?? '—'}</span> → <span className="text-gray-800">{String(c.to ?? '—')}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkTimelineModal;
